import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { BookOpen, Plus, Search, Edit, Trash2, Users, UserPlus } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { createApiUrl } from "@/lib/api";

interface ClassSchedule {
  start_time: string;
  end_time: string;
}

interface Class {
  id: string;
  name: string;
  description: string;
  teacher_ids: string[];
  schedule: {
    monday?: ClassSchedule;
    tuesday?: ClassSchedule;
    wednesday?: ClassSchedule;
    thursday?: ClassSchedule;
    friday?: ClassSchedule;
    saturday?: ClassSchedule;
    sunday?: ClassSchedule;
  };
  subject: string;
  grade: string;
  max_students: number;
  fees: number;
  admin_id: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface Teacher {
  id: string;
  name: string;
  email: string;
  phone: string;
  subject: string;
}

interface Student {
  id: string;
  name: string;
  email: string;
  phone: string;
  class_id: string | null;
  assigned_teacher_id: string | null;
}

const AdminClasses = () => {
  const [classes, setClasses] = useState<Class[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState<Class | null>(null);
  const [selectedTeacher, setSelectedTeacher] = useState<string>("");
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [newClass, setNewClass] = useState({
    name: "",
    description: "",
    teacher_ids: [] as string[],
    subject: "",
    grade: "",
    max_students: 30,
    fees: 0,
    schedule: {
      monday: { start_time: "", end_time: "" },
      tuesday: { start_time: "", end_time: "" },
      wednesday: { start_time: "", end_time: "" },
      thursday: { start_time: "", end_time: "" },
      friday: { start_time: "", end_time: "" },
      saturday: { start_time: "", end_time: "" },
      sunday: { start_time: "", end_time: "" },
    }
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchClasses();
    fetchTeachers();
    fetchStudents();
  }, []);

  const fetchClasses = async () => {
    try {
      setIsLoading(true);
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        toast({
          title: "Error",
          description: "Please login again",
          variant: "destructive"
        });
        return;
      }

      const response = await fetch(createApiUrl('/admin/classes?skip=0&limit=10'), {
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setClasses(data.classes || []);
      } else if (response.status === 401) {
        toast({
          title: "Session Expired",
          description: "Please login again",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to fetch classes",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
      toast({
        title: "Error",
        description: "Failed to connect to server",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTeachers = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) return;

      const response = await fetch(createApiUrl('/admin/teachers?skip=0&limit=10'), {
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTeachers(data.teachers || []);
      }
    } catch (error) {
      console.error('Error fetching teachers:', error);
    }
  };

  const fetchStudents = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) return;

      const response = await fetch(createApiUrl('/admin/students?skip=0&limit=10'), {
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStudents(data.students || []);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const filteredClasses = classes.filter(cls =>
    cls.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cls.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cls.grade.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddClass = async () => {
    if (!newClass.name || !newClass.subject || !newClass.grade) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    // Filter out empty schedule slots
    const filteredSchedule = Object.entries(newClass.schedule).reduce((acc, [day, schedule]) => {
      if (schedule.start_time && schedule.end_time) {
        acc[day] = schedule;
      }
      return acc;
    }, {} as any);

    const classData = {
      name: newClass.name,
      description: newClass.description,
      teacher_ids: newClass.teacher_ids,
      schedule: filteredSchedule,
      subject: newClass.subject,
      grade: newClass.grade,
      max_students: newClass.max_students,
      fees: newClass.fees
    };

    try {
      const response = await fetch(createApiUrl('/admin/classes'), {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiI2ODVhNjQyYTM3YjkxYmQxMGQzNWUyMzEiLCJyb2xlIjoiYWRtaW4iLCJleHAiOjE3NTA3ODc3MjJ9.beqM8gKZWesLgHIWJajxndS9jluIKi6m3O8zMKAZs0Y',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(classData)
      });

      if (response.ok) {
        const createdClass = await response.json();
        setClasses([...classes, createdClass]);
        toast({
          title: "Class Added!",
          description: `${createdClass.name} has been created successfully`
        });

        // Reset form
        setNewClass({
          name: "",
          description: "",
          teacher_ids: [],
          subject: "",
          grade: "",
          max_students: 30,
          fees: 0,
          schedule: {
            monday: { start_time: "", end_time: "" },
            tuesday: { start_time: "", end_time: "" },
            wednesday: { start_time: "", end_time: "" },
            thursday: { start_time: "", end_time: "" },
            friday: { start_time: "", end_time: "" },
            saturday: { start_time: "", end_time: "" },
            sunday: { start_time: "", end_time: "" },
          }
        });
        setIsAddDialogOpen(false);
      } else {
        toast({
          title: "Error",
          description: "Failed to create class",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error creating class:', error);
      toast({
        title: "Error",
        description: "Failed to connect to server",
        variant: "destructive"
      });
    }
  };

  const getTeacherNames = (teacherIds: string[]) => {
    return teacherIds.map(id => {
      const teacher = teachers.find(t => t.id === id);
      return teacher ? teacher.name : 'Unknown';
    }).join(', ');
  };

  const formatSchedule = (schedule: any) => {
    const days = Object.keys(schedule);
    if (days.length === 0) return 'Not scheduled';
    
    return days.map(day => 
      `${day.charAt(0).toUpperCase() + day.slice(1)}: ${schedule[day].start_time}-${schedule[day].end_time}`
    ).join(', ');
  };

  const handleAssignStudents = async () => {
    if (!selectedClass || !selectedTeacher || selectedStudents.length === 0) {
      toast({
        title: "Missing Information",
        description: "Please select a teacher and at least one student",
        variant: "destructive"
      });
      return;
    }

    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) return;

      // Assign each selected student
      const assignPromises = selectedStudents.map(studentId =>
        fetch(createApiUrl(`/admin/students/${studentId}/assign`), {
          method: 'PUT',
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            teacher_id: selectedTeacher,
            class_id: selectedClass.id
          })
        })
      );

      const results = await Promise.all(assignPromises);
      const allSuccessful = results.every(response => response.ok);

      if (allSuccessful) {
        toast({
          title: "Students Assigned",
          description: "Selected students have been assigned successfully"
        });
        setIsAssignDialogOpen(false);
        setSelectedStudents([]);
        setSelectedTeacher("");
        fetchStudents(); // Refresh the students list
      } else {
        throw new Error('Failed to assign some students');
      }
    } catch (error) {
      console.error('Error assigning students:', error);
      toast({
        title: "Error",
        description: "Failed to assign students",
        variant: "destructive"
      });
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading classes...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-red-600" />
              Classes
            </h1>
            <p className="text-gray-600">Manage classes and assignments</p>
          </div>

          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-red-600 hover:bg-red-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Class
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Class</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Class Name *</Label>
                    <Input
                      id="name"
                      value={newClass.name}
                      onChange={(e) => setNewClass({...newClass, name: e.target.value})}
                      placeholder="e.g., Advanced Mathematics"
                    />
                  </div>
                  <div>
                    <Label htmlFor="subject">Subject *</Label>
                    <Input
                      id="subject"
                      value={newClass.subject}
                      onChange={(e) => setNewClass({...newClass, subject: e.target.value})}
                      placeholder="e.g., Mathematics"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={newClass.description}
                    onChange={(e) => setNewClass({...newClass, description: e.target.value})}
                    placeholder="Course description"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="grade">Grade *</Label>
                    <Input
                      id="grade"
                      value={newClass.grade}
                      onChange={(e) => setNewClass({...newClass, grade: e.target.value})}
                      placeholder="e.g., 11th Grade"
                    />
                  </div>
                  <div>
                    <Label htmlFor="max_students">Max Students</Label>
                    <Input
                      id="max_students"
                      type="number"
                      value={newClass.max_students}
                      onChange={(e) => setNewClass({...newClass, max_students: parseInt(e.target.value) || 30})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="fees">Fees</Label>
                    <Input
                      id="fees"
                      type="number"
                      value={newClass.fees}
                      onChange={(e) => setNewClass({...newClass, fees: parseFloat(e.target.value) || 0})}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div>
                  <Label>Schedule (Optional)</Label>
                  <div className="grid grid-cols-1 gap-2 mt-2">
                    {Object.entries(newClass.schedule).map(([day, schedule]) => (
                      <div key={day} className="flex items-center gap-2">
                        <div className="w-20 text-sm capitalize">{day}:</div>
                        <Input
                          placeholder="Start time"
                          type="time"
                          value={schedule.start_time}
                          onChange={(e) => setNewClass({
                            ...newClass,
                            schedule: {
                              ...newClass.schedule,
                              [day]: { ...schedule, start_time: e.target.value }
                            }
                          })}
                          className="flex-1"
                        />
                        <Input
                          placeholder="End time"
                          type="time"
                          value={schedule.end_time}
                          onChange={(e) => setNewClass({
                            ...newClass,
                            schedule: {
                              ...newClass.schedule,
                              [day]: { ...schedule, end_time: e.target.value }
                            }
                          })}
                          className="flex-1"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <Button onClick={handleAddClass} className="w-full">
                  Add Class
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>All Classes ({classes.length})</CardTitle>
            <p className="text-sm text-gray-600">View and manage all classes</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
              <Input
                placeholder="Search Classes"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Class Name</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Grade</TableHead>
                  <TableHead>Teachers</TableHead>
                  <TableHead>Schedule</TableHead>
                  <TableHead>Fees</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClasses.map((cls) => (
                  <TableRow key={cls.id}>
                    <TableCell className="font-medium">{cls.name}</TableCell>
                    <TableCell>{cls.subject}</TableCell>
                    <TableCell>{cls.grade}</TableCell>
                    <TableCell>
                      <div className="text-sm text-gray-600">
                        {getTeacherNames(cls.teacher_ids) || 'No teacher assigned'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-xs text-gray-500 max-w-xs truncate">
                        {formatSchedule(cls.schedule)}
                      </div>
                    </TableCell>
                    <TableCell>₹{cls.fees}</TableCell>
                    <TableCell>
                      <Badge className={cls.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                        {cls.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedClass(cls);
                            setSelectedTeacher(cls.teacher_ids[0] || "");
                            setIsAssignDialogOpen(true);
                          }}
                        >
                          <UserPlus className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedClass(cls);
                            setIsAssignDialogOpen(true);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:text-red-900 hover:bg-red-50"
                          onClick={() => {
                            setSelectedClass(cls);
                            setIsAssignDialogOpen(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Add Assignment Dialog */}
      <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Assign Students to Class</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Class</Label>
              <p className="text-sm text-gray-500">{selectedClass?.name} - {selectedClass?.grade}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="teacher">Select Teacher</Label>
              <Select
                value={selectedTeacher}
                onValueChange={setSelectedTeacher}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a teacher" />
                </SelectTrigger>
                <SelectContent>
                  {teachers.map((teacher) => (
                    <SelectItem key={teacher.id} value={teacher.id}>
                      {teacher.name} - {teacher.subject}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Select Students</Label>
              <div className="border rounded-md p-4 max-h-60 overflow-y-auto">
                {students
                  .filter(student => !student.class_id) // Show only unassigned students
                  .map((student) => (
                    <div key={student.id} className="flex items-center space-x-2 py-2">
                      <input
                        type="checkbox"
                        id={student.id}
                        checked={selectedStudents.includes(student.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedStudents([...selectedStudents, student.id]);
                          } else {
                            setSelectedStudents(selectedStudents.filter(id => id !== student.id));
                          }
                        }}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <label htmlFor={student.id} className="text-sm">
                        {student.name} - {student.email}
                      </label>
                    </div>
                  ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => setIsAssignDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={() => handleAssignStudents()}>
              Assign Students
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminClasses;
